import type { IApplicationAttribution, IApplicationUtm } from '@/context/borrower';
import { EParameters } from '@/utils/setParametersFromParams';

/**
 * Reads the marketing tags off the landing URL (SR-2784).
 *
 * Only the keys we know about are read — never the whole query string — because
 * a host page's URL can carry personal data, which is why getEmbedContext
 * deliberately strips query strings from the parent URL it records.
 *
 * Returns undefined when nothing was passed, so an application without tags
 * sends no attribution at all. The API sanitises and caps whatever it receives.
 */
export function getAttributionFromParams(
    params: URLSearchParams
): IApplicationAttribution | undefined {
    // Built here rather than at module scope: this file is reached through the
    // setParametersFromParams barrel, which imports it back, so EParameters is
    // still undefined while this module is initialising. Reading it at call time
    // sidesteps that cycle — a module-scope table silently broke the whole app.
    const utmParams: [keyof IApplicationUtm, EParameters][] = [
        ['source', EParameters.UTM_SOURCE],
        ['medium', EParameters.UTM_MEDIUM],
        ['campaign', EParameters.UTM_CAMPAIGN],
        ['content', EParameters.UTM_CONTENT],
        ['term', EParameters.UTM_TERM],
        ['id', EParameters.UTM_ID],
    ];

    const utm: IApplicationUtm = {};

    utmParams.forEach(([key, param]) => {
        const value = params.get(param);
        if (value !== null) utm[key] = value;
    });

    const gclid = params.get(EParameters.GCLID);

    const attribution: IApplicationAttribution = {};
    if (Object.keys(utm).length > 0) attribution.utm = utm;
    if (gclid !== null) attribution.clickIds = { gclid };

    return Object.keys(attribution).length > 0 ? attribution : undefined;
}
